/**
 * Acceso al catálogo de Productos con PAGINACIÓN / FILTRO / ORDEN SERVER-SIDE.
 *
 * POR QUÉ: antes la lista de Productos traía el catálogo entero con `.limit(10000)` y
 * filtraba/buscaba/ordenaba/paginaba EN MEMORIA. Pero PostgREST corta en 1000 filas
 * (max-rows), así que ese `.limit(10000)` es ficción: al cruzar las 1000 filas el catálogo
 * se TRUNCA EN SILENCIO — la búsqueda no encuentra productos más allá del #1000 y los
 * totales quedan mal, sin ningún error. Con `.range()` + `count: 'exact'` el tamaño del
 * catálogo deja de importar: se pide solo la página visible y el contador es exacto.
 *
 * Mismo patrón que `customersApi.ts` (Clientes, ya migrado).
 */
import { coincideConVista } from '@/lib/products/productStatus';
import { supabase } from '@/lib/supabase';

export interface ProductRow {
  id: string;
  name: string;
  description: string;
  price: number;
  /** Precio de comparación (precio tachado). null si no está en oferta. */
  compare_at_price: number | null;
  category: string;
  image_url: string;
  stock: number;
  is_active: boolean;
  /** Cuándo se archivó. NULL = no archivado. Gana sobre `is_active` (ver productStatus.ts). */
  archived_at: string | null;
  show_in_store: boolean;
}

const PRODUCT_COLUMNS = 'id,name,description,price,compare_at_price,category,image_url,stock,is_active,show_in_store,archived_at';

const num = (v: unknown) => Number(v) || 0;
/** Igual que num pero preserva null (para compare_at_price: "sin oferta" ≠ "S/ 0"). */
const numOrNull = (v: unknown) => (v == null || v === '' ? null : Number(v) || null);

function mapProductRow(r: Record<string, unknown>): ProductRow {
  return {
    id: String(r.id),
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    price: num(r.price),
    compare_at_price: numOrNull(r.compare_at_price),
    category: (r.category as string) ?? '',
    image_url: (r.image_url as string) ?? '',
    stock: num(r.stock),
    is_active: !!r.is_active,
    show_in_store: !!r.show_in_store,
    archived_at: (r.archived_at as string | null) ?? null,
  };
}

export type ProductSortKey = 'name' | 'category';

/** Orden permitido → columna real. Allowlist: nunca interpolar input del usuario en .order(). */
const SORTABLE_COLUMNS: Record<ProductSortKey, string> = {
  name: 'name',
  category: 'category',
};

/**
 * Vista de estado (las pestañas del listado).
 *
 * 🪤 "Archivado" se deducía de `is_active = false AND show_in_store = true` — un interruptor de
 * CANAL usado como etapa del ciclo de vida, y al revés: dejaba el producto visible en la tienda.
 * Ahora hay columna propia (`archived_at`) y la semántica vive en `@/lib/products/productStatus`.
 *
 *  - all      → activos y borradores (los archivados NO aparecen)
 *  - active   → no archivado Y is_active
 *  - draft    → no archivado Y !is_active
 *  - archived → archived_at != null
 */
export type ProductStatusView = 'all' | 'active' | 'draft' | 'archived';

/** Escapa los comodines de PostgREST para que la búsqueda no se pueda inyectar. */
function sanitizeSearch(q: string): string {
  return q.trim().replace(/[%,()]/g, ' ').replace(/\s+/g, ' ');
}

export interface ProductFilter {
  search?: string;
  statusView?: ProductStatusView;
  statusFilter?: string[];
  categories?: string[];
  sortBy?: ProductSortKey;
  sortDir?: 'asc' | 'desc';
}

/**
 * ¿El producto matchea la vista de estado? Fuente ÚNICA de la semántica de las pestañas,
 * compartida por el filtro server-side (traducido a `.eq()`) y el fallback offline en memoria.
 */
export function matchesProductStatus(p: ProductRow, view: ProductStatusView, statusFilter: string[]): boolean {
  if (!coincideConVista(p, view)) return false;
  // Dentro de "todos", el ChoiceList todavía puede acotar por activo/inactivo.
  if (view === 'all' && statusFilter.length === 1) {
    if (statusFilter[0] === 'active') return p.is_active;
    if (statusFilter[0] === 'inactive') return !p.is_active;
  }
  return true;
}

/** Filtra + ordena en memoria con la MISMA semántica que la query server-side (fallback offline). */
export function filterSortProducts(all: ProductRow[], f: ProductFilter): ProductRow[] {
  const view = f.statusView ?? 'all';
  const statusFilter = f.statusFilter ?? [];
  const categories = f.categories ?? [];
  const term = (f.search ?? '').trim().toLowerCase();
  const result = all.filter(
    (x) =>
      matchesProductStatus(x, view, statusFilter) &&
      (categories.length === 0 || categories.includes(x.category || '')) &&
      (term === '' || x.name.toLowerCase().includes(term)),
  );
  const asc = (f.sortDir ?? 'asc') === 'asc';
  const key: ProductSortKey = f.sortBy ?? 'name';
  result.sort((a, b) => {
    const cmp = (a[key] || '').localeCompare(b[key] || '') || a.id.localeCompare(b.id);
    return asc ? cmp : -cmp;
  });
  return result;
}

export interface ProductsPageParams {
  tenantId: string;
  offset: number;
  limit: number;
  search?: string;
  statusView?: ProductStatusView;
  /** Filtro de estado adicional (ChoiceList) — solo aplica en la vista 'all'. */
  statusFilter?: string[];
  /** Categorías seleccionadas (por nombre). Vacío = todas. */
  categories?: string[];
  sortBy?: ProductSortKey;
  sortDir?: 'asc' | 'desc';
}

export interface ProductsPageResult {
  items: ProductRow[];
  /** Total REAL de productos que matchean el filtro (count exacto del servidor). */
  total: number;
}

/** Una PÁGINA de productos del tenant, paginada/filtrada/ordenada EN EL SERVIDOR. */
export async function fetchProductsPage(params: ProductsPageParams): Promise<ProductsPageResult> {
  const {
    tenantId, offset, limit, search, statusView = 'all',
    statusFilter = [], categories = [], sortBy = 'name', sortDir = 'asc',
  } = params;

  let q = supabase
    .from('products')
    .select(PRODUCT_COLUMNS, { count: 'exact' })
    .eq('tenant_id', tenantId);

  // Vista de estado (pestaña). En 'all' se aplica el filtro de estado del ChoiceList.
  switch (statusView) {
    case 'active':
      q = q.is('archived_at', null).eq('is_active', true);
      break;
    case 'draft':
      q = q.is('archived_at', null).eq('is_active', false);
      break;
    case 'archived':
      q = q.not('archived_at', 'is', null);
      break;
    case 'all':
    default:
      // Los archivados no ensucian las vistas normales: para eso se archivan.
      q = q.is('archived_at', null);
      if (statusFilter.length === 1) {
        if (statusFilter[0] === 'active') q = q.eq('is_active', true);
        else if (statusFilter[0] === 'inactive') q = q.eq('is_active', false);
      }
      break;
  }

  if (categories.length > 0) q = q.in('category', categories);

  const term = sanitizeSearch(search ?? '');
  if (term) q = q.ilike('name', `%${term}%`);

  const column = SORTABLE_COLUMNS[sortBy] ?? SORTABLE_COLUMNS.name;
  const { data, error, count } = await q
    .order(column, { ascending: sortDir === 'asc', nullsFirst: false })
    .order('id', { ascending: true }) // desempate estable entre páginas
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return {
    items: (data ?? []).map((r) => mapProductRow(r as Record<string, unknown>)),
    total: count ?? 0,
  };
}

/**
 * TODOS los productos que matchean el filtro (para exportar). Pagina en bloques de 1000
 * con `.range()` hasta traer el `total` real — así el export NO trunca en 1000 como antes.
 */
export async function fetchAllProductsMatching(
  params: Omit<ProductsPageParams, 'offset' | 'limit'>,
): Promise<ProductRow[]> {
  const CHUNK = 1000;
  const all: ProductRow[] = [];
  let offset = 0;
  for (;;) {
    const page = await fetchProductsPage({ ...params, offset, limit: CHUNK });
    all.push(...page.items);
    if (page.items.length === 0 || all.length >= page.total) break;
    offset += CHUNK;
  }
  return all;
}

/** Productos por id (para exportar una selección que puede cruzar páginas). */
export async function fetchProductsByIds(tenantId: string, ids: string[]): Promise<ProductRow[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_COLUMNS)
    .eq('tenant_id', tenantId)
    .in('id', ids);
  if (error) throw error;
  return (data ?? []).map((r) => mapProductRow(r as Record<string, unknown>));
}

/**
 * TODAS las filas de `products` del tenant con las columnas pedidas, paginando con
 * `.range()` en bloques de 1000.
 *
 * POR QUÉ: las páginas que cuentan productos por categoría/etiqueta/modificador hacían
 * `.select('category')` (o 'tags', 'id') SIN paginar. PostgREST corta en 1000 → los
 * `product_count` SUB-CONTABAN en silencio pasando esa marca. El orden estable por `id` es
 * obligatorio: sin él, dos filas empatadas pueden saltar de página y perderse.
 *
 * NOTA de escala: correcto a cualquier tamaño, pero trae el catálogo al cliente. Contar en
 * SQL (RPC / aggregates) es el siguiente paso para catálogos grandes.
 */
export async function fetchAllProductRows<T>(tenantId: string, columns: string): Promise<T[]> {
  const PAGE = 1000;
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('products')
      .select(columns)
      .eq('tenant_id', tenantId)
      .order('id') // desempate estable entre páginas
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as T[];
    all.push(...rows);
    if (rows.length < PAGE) break;
  }
  return all;
}

/**
 * Pagina cualquier query con `.range()` hasta traer todas las filas (PostgREST corta en
 * 1000). `makeQuery` debe incluir un `.order()` estable (p. ej. por `id`): sin él, dos filas
 * empatadas pueden saltar de página y perderse.
 */
export async function fetchAllPaged<T>(
  makeQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const PAGE = 1000;
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await makeQuery(from, from + PAGE - 1);
    if (error) throw error;
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < PAGE) break;
  }
  return all;
}

/**
 * Filas de una tabla hija (product_modifiers, product_variants, …) para esos productos,
 * troceando los ids (evita URLs gigantes → 414) y paginando cada trozo (PostgREST corta
 * en 1000). Antes se hacía `.in('product_id', todosLosIds)` sin paginar: truncaba.
 */
export async function fetchAllByProductIds<T>(
  table: string,
  columns: string,
  productIds: string[],
): Promise<T[]> {
  const PAGE = 1000;
  const ID_CHUNK = 300;
  const rows: T[] = [];
  if (productIds.length === 0) return rows;
  for (let i = 0; i < productIds.length; i += ID_CHUNK) {
    const ids = productIds.slice(i, i + ID_CHUNK);
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from(table)
        .select(columns)
        .in('product_id', ids)
        .order('id') // desempate estable entre páginas
        .range(from, from + PAGE - 1);
      if (error) throw error;
      const page = (data ?? []) as T[];
      rows.push(...page);
      if (page.length < PAGE) break;
    }
  }
  return rows;
}

export interface ProductsStats {
  total: number;
  active: number;
  draft: number;
  archived: number;
}

/** Conteos de las pestañas, exactos (count/head) sobre el conjunto COMPLETO — no truncan. */
export async function fetchProductsStats(tenantId: string): Promise<ProductsStats> {
  const base = () => supabase.from('products').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId);
  const [totalRes, activeRes, draftRes, archivedRes] = await Promise.all([
    base().is('archived_at', null),
    base().is('archived_at', null).eq('is_active', true),
    base().is('archived_at', null).eq('is_active', false),
    base().not('archived_at', 'is', null),
  ]);
  return {
    total: totalRes.count ?? 0,
    active: activeRes.count ?? 0,
    draft: draftRes.count ?? 0,
    archived: archivedRes.count ?? 0,
  };
}

/**
 * Total de inventario (suma de existencias en sucursales activas) SOLO para los productos
 * de la página visible. Acotado a esos `productIds` → nunca se acerca al límite de 1000.
 */
export async function fetchInventoryTotals(
  tenantId: string,
  productIds: string[],
): Promise<Record<string, number>> {
  const totals: Record<string, number> = {};
  if (productIds.length === 0) return totals;

  const { data: branchRows } = await supabase
    .from('branches')
    .select('id')
    .eq('tenant_id', tenantId)
    .eq('is_active', true);
  const branchIds = (branchRows ?? []).map((b: { id: string }) => b.id);
  if (branchIds.length === 0) return totals;

  const { data: invRows, error } = await supabase
    .from('inventory')
    .select('product_id, quantity')
    .in('product_id', productIds)
    .in('branch_id', branchIds);
  if (error) throw error;

  (invRows ?? []).forEach((r: { product_id: string; quantity: number }) => {
    totals[r.product_id] = (totals[r.product_id] || 0) + num(r.quantity);
  });
  return totals;
}

/** Estado de un producto en un marketplace, para la página visible de la lista. */
export interface MarketplaceLinkState {
  /** 'active' | 'paused' | 'closed' | 'error' | … tal cual lo reporta el canal. */
  status: string | null;
  /** Enlace público de la publicación, si existe. */
  url: string | null;
  /** Último error del canal; es lo que explica por qué algo "no se publicó". */
  error: string | null;
}

export interface ProductMarketplaceState {
  mercadolibre?: MarketplaceLinkState;
  falabella?: MarketplaceLinkState;
}

/**
 * Estado en marketplaces de los productos de la página visible.
 *
 * Va aparte de `fetchProductsPage` a propósito: son tablas distintas y solo hacen falta para el
 * desplegable de la columna Canales. Se piden por los ids ya paginados — nunca por el catálogo
 * entero, que es como se cruzaban las 1000 filas de PostgREST.
 *
 * Un fallo acá NO rompe la lista: el desplegable muestra lo que sí sabe (Tienda y POS) y omite los
 * marketplaces. Que no se pueda leer Mercado Libre no es razón para dejar al vendedor sin catálogo.
 */
export async function fetchMarketplaceStates(
  tenantId: string,
  productIds: string[],
): Promise<Record<string, ProductMarketplaceState>> {
  const byProduct: Record<string, ProductMarketplaceState> = {};
  if (productIds.length === 0) return byProduct;

  const [meli, fala] = await Promise.all([
    supabase
      .from('meli_item_links')
      .select('product_id, status, permalink, last_error')
      .eq('tenant_id', tenantId)
      .in('product_id', productIds),
    supabase
      .from('falabella_listings')
      .select('product_id, status, url, last_error')
      .eq('tenant_id', tenantId)
      .in('product_id', productIds),
  ]);

  (meli.data ?? []).forEach((r: { product_id: string; status: string | null; permalink: string | null; last_error: string | null }) => {
    byProduct[r.product_id] = {
      ...byProduct[r.product_id],
      mercadolibre: { status: r.status, url: r.permalink, error: r.last_error },
    };
  });
  (fala.data ?? []).forEach((r: { product_id: string; status: string | null; url: string | null; last_error: string | null }) => {
    byProduct[r.product_id] = {
      ...byProduct[r.product_id],
      falabella: { status: r.status, url: r.url, error: r.last_error },
    };
  });
  return byProduct;
}
